import type { TvChannelDay } from '@lib/graphql/generated';
import { createDate } from '@utils/dateHelpers';
import { addDays, differenceInMilliseconds, isAfter, isBefore, set, startOfDay } from 'date-fns';
import { memo, useMemo } from 'react';
import { TIMESCALE } from 'tvm-config';
import { TimelineProgramEntry } from './TimelineProgramEntry';

export interface TimelineChannelProgramProps {
  channelEntry: TvChannelDay;
  activeDay: Date;
  currentTime: Date;
  windowStart: number;
  windowEnd: number;
}

export const TimelineChannelProgram = memo(
  ({ channelEntry, activeDay, currentTime, windowStart, windowEnd }: TimelineChannelProgramProps) => {
    const showtimes = channelEntry?.showtimes;
    const channel = channelEntry?.channel;

    const positioned = useMemo(() => {
      if (!showtimes?.length || !activeDay) return [];
      const dayStart = startOfDay(activeDay);
      const dayEnd = set(addDays(activeDay, 1), { hours: 6, minutes: 0, seconds: 0, milliseconds: 0 });
      const items: { showtime: (typeof showtimes)[number]; left: number; width: number }[] = [];
      for (const showtime of showtimes) {
        const start = createDate(showtime.start);
        if (isBefore(start, dayStart) || isAfter(start, dayEnd)) continue;
        const offset = differenceInMilliseconds(start, dayStart) / 1000 / 60;
        if (!offset) continue;
        const duration = (createDate(showtime.stop).getTime() - start.getTime()) / 1000 / 60;
        items.push({ showtime, left: offset * TIMESCALE, width: duration * TIMESCALE });
      }
      return items;
    }, [showtimes, activeDay]);

    return (
      <div className="relative h-[72px] md:h-28">
        {positioned.map((item) =>
          item.left < windowEnd && item.left + item.width > windowStart ? (
            <TimelineProgramEntry
              channel={channel}
              entry={item.showtime}
              currentTime={currentTime}
              left={item.left}
              width={item.width}
              key={item.showtime.id}
            />
          ) : null,
        )}
      </div>
    );
  },
);

TimelineChannelProgram.displayName = 'TimelineChannelProgram';
